// An organisation's custom apps (spec/13 §9, spec/04 §5; F-CAT-02).
//
//   GET   /v1/organizations/{o}/custom-apps                                   the org's custom apps     library:read
//   POST  /v1/organizations/{o}/custom-apps                                   create one                library:manage
//   PATCH /v1/organizations/{o}/custom-apps/{customAppId}                     edit one                  library:manage
//   POST  /v1/organizations/{o}/custom-apps/{customAppId}/archive             archive one               library:manage
//   POST  /v1/organizations/{o}/custom-apps/{customAppId}/icon                signed icon upload        library:manage
//   POST  /v1/organizations/{o}/custom-apps/{customAppId}/icon/{uploadId}/complete   confirm it        library:manage
//   GET   /v1/organizations/{o}/venues/{v}/custom-apps                        pick one to add here      library:read
//
// "Custom apps not in the catalogue: name, icon, launch type and path, arguments, working directory…"
// (spec/13 §9). A custom app is the **organisation's own** title — a venue's tournament client, a local
// tool, a browser build HQ never added — so it is org data, never a row of HQ's platform catalogue
// (`game`). It belongs to the organisation and is usable at every venue of it: a venue offers it by adding
// it to its library (`POST …/venues/{v}/library` with `{ customAppId }`), exactly as it adds a catalogue
// title, and the station's signed bundle then carries it as a `kind: 'app'` entry with its recipes, icon
// and detection rules — so the station launches and detects it, and the kiosk shows it, unchanged.
//
// **Launch recipes are the catalogue's** (`LaunchRecipe`: exe/uri/store/lnk), with one extra rule: a path
// may only use the path variables a station resolves (`LAUNCH_PATH_VARIABLES`); anything else in braces is a
// `400`, because the station would refuse to launch it (spec/10 §5.2, fail closed).
//
// **Archive, not delete.** Archiving hides a custom app from the add picker and takes it out of every
// station's bundle; library entries that offer it stay (shown as archived in the console) until a venue
// removes them. Every write is audited (spec/07 §11).
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { CustomAppIdSchema, LibraryEntryIdSchema, OrganizationIdSchema } from "../ids/schema.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, jsonResponse, problemResponse } from "../openapi/responses.js";
import { LIBRARY_MANAGE, LIBRARY_READ } from "../permissions/index.js";
import { PageQuerySchema, paginated } from "../primitives/page.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema, VenueScopeParamsSchema } from "../primitives/scope.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { AppCategorySchema, AppIconUploadSchema, CreateAppIconUploadSchema, LaunchRecipeSchema, TitleDetectionInputSchema, TitleDetectionSchema, } from "./hq-catalogue.js";
// ---------------------------------------------------------------------------------------------
// Path variables (spec/10 §5.2)
// ---------------------------------------------------------------------------------------------
/**
 * The path variables a station resolves in a launch or detection path (spec/10 §5.2,
 * `station/Core/Launching/PathVariableExpander.cs`): `{ProgramFiles}`, `{UserProfile}`, `{SteamLibrary}` and
 * `{EpicInstall:<AppName>}`. The console lists them beside the path field.
 */
export const LAUNCH_PATH_VARIABLES = ['{ProgramFiles}', '{UserProfile}', '{SteamLibrary}', '{EpicInstall:<AppName>}'];
const SIMPLE_VARIABLES = new Set(['programfiles', 'userprofile', 'steamlibrary']);
/**
 * The brace expressions in `path` a station would not resolve — an unknown variable, an empty `{}`, an
 * `{EpicInstall:}` with no app name, or an unterminated `{`. Empty when every variable is supported. Names
 * compare case-insensitively, as the station's expander does.
 */
export function unsupportedPathVariables(path) {
    const bad = [];
    let index = 0;
    while (index < path.length) {
        const open = path.indexOf('{', index);
        const stray = path.indexOf('}', index);
        if (stray !== -1 && (open === -1 || stray < open)) {
            bad.push('}');
            index = stray + 1;
            continue;
        }
        if (open === -1)
            break;
        const close = path.indexOf('}', open + 1);
        if (close === -1) {
            bad.push(path.slice(open));
            break;
        }
        const inner = path.slice(open + 1, close);
        const colon = inner.indexOf(':');
        const name = (colon === -1 ? inner : inner.slice(0, colon)).trim().toLowerCase();
        const argument = colon === -1 ? undefined : inner.slice(colon + 1).trim();
        const ok = colon === -1 ? SIMPLE_VARIABLES.has(name) : name === 'epicinstall' && argument !== undefined && argument !== '' && !argument.includes('{');
        if (!ok)
            bad.push(path.slice(open, close + 1));
        index = close + 1;
    }
    return bad;
}
/** The recipe fields that hold a path on the PC, which may only use the variables a station resolves. */
const RECIPE_PATH_FIELDS = ['path', 'workingDirectory'];
/** Adds an issue for every recipe path that uses a variable a station would not resolve. */
function checkRecipePaths(recipes, ctx) {
    recipes.forEach((recipe, index) => {
        for (const field of RECIPE_PATH_FIELDS) {
            const value = recipe[field];
            if (value === undefined)
                continue;
            const bad = unsupportedPathVariables(value);
            if (bad.length > 0) {
                ctx.addIssue({
                    code: 'custom',
                    path: [index, field],
                    message: `Unsupported path variable ${bad.join(', ')}. Use ${LAUNCH_PATH_VARIABLES.join(', ')}.`,
                });
            }
        }
    });
}
/** Adds an issue when the detection path pattern uses a variable a station would not resolve. */
function checkDetectionPath(detection, ctx) {
    const pattern = detection?.pathPattern;
    if (pattern === undefined || pattern === null)
        return;
    const bad = unsupportedPathVariables(pattern);
    if (bad.length > 0)
        ctx.addIssue({ code: 'custom', path: ['detection', 'pathPattern'], message: `Unsupported path variable ${bad.join(', ')}.` });
}
/** At most this many ways to launch one custom app. */
export const MAX_CUSTOM_APP_RECIPES = 4;
const customAppNameSchema = z.string().trim().min(1).max(120).openapi({ description: 'The app’s name, as players see it.', example: 'Tournament client' });
const customAppRecipesSchema = z
    .array(LaunchRecipeSchema)
    .min(1)
    .max(MAX_CUSTOM_APP_RECIPES)
    .superRefine(checkRecipePaths)
    .openapi({ description: 'How a station launches it, in preference order (spec/10 §5.2). At least one.' });
// ---------------------------------------------------------------------------------------------
// The shapes
// ---------------------------------------------------------------------------------------------
/** An organisation's custom app (spec/04 §5, spec/13 §9). */
export const CustomAppSchema = z
    .object({
    id: CustomAppIdSchema,
    organizationId: OrganizationIdSchema,
    name: z.string(),
    /** The kiosk category a venue most likely files it under; the library add defaults to it. */
    suggestedCategory: AppCategorySchema,
    /** The square icon on our media CDN, or `null` (the kiosk then draws the initial). */
    iconUrl: z.string().url().nullable(),
    /** How a station launches it (spec/10 §5.2). */
    launchRecipes: z.array(LaunchRecipeSchema),
    /** How a station recognises its process (spec/10 §7.3). */
    detection: TitleDetectionSchema,
    /** When it was archived, or `null` while it is in use. An archived app is in no station's bundle. */
    archivedAt: IsoDateTimeSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .openapi('CustomApp', { description: 'An organisation’s own app, not in the platform catalogue (spec/13 §9).' });
export const CustomAppListSchema = paginated(CustomAppSchema, 'CustomAppList');
/** The body of `POST …/custom-apps`. The name is unique among the organisation's unarchived custom apps. */
export const CreateCustomAppSchema = z
    .object({
    name: customAppNameSchema,
    suggestedCategory: AppCategorySchema,
    launchRecipes: customAppRecipesSchema,
    /** Absent: nothing but a launch from the kiosk (no process names). */
    detection: TitleDetectionInputSchema.optional(),
})
    .strict()
    .superRefine((body, ctx) => checkDetectionPath(body.detection, ctx))
    .openapi('CreateCustomApp', { description: 'Create an organisation’s custom app (spec/13 §9).' });
/** The body of `PATCH …/custom-apps/{customAppId}`: only the fields sent change; `launchRecipes` replaces the set. */
export const UpdateCustomAppSchema = z
    .object({
    name: customAppNameSchema,
    suggestedCategory: AppCategorySchema,
    launchRecipes: customAppRecipesSchema,
    detection: TitleDetectionInputSchema,
})
    .partial()
    .strict()
    .refine((patch) => Object.keys(patch).length > 0, { message: 'Send at least one field to change.' })
    .superRefine((patch, ctx) => checkDetectionPath(patch.detection, ctx))
    .openapi('UpdateCustomApp', { description: 'Edit a custom app — only the fields sent change.' });
export const CustomAppParamsSchema = z
    .object({ organizationId: OrganizationIdSchema, customAppId: CustomAppIdSchema })
    .openapi('CustomAppParams');
export const CustomAppIconUploadParamsSchema = z
    .object({ organizationId: OrganizationIdSchema, customAppId: CustomAppIdSchema, uploadId: z.uuid() })
    .openapi('CustomAppIconUploadParams');
/** One of the organisation's custom apps as a venue's add picker sees it: the app, and whether this venue offers it. */
export const VenueCustomAppSchema = z
    .object({
    customApp: CustomAppSchema,
    inLibrary: z.boolean(),
    /** The venue library entry that offers it, when `inLibrary`; `null` otherwise. */
    entryId: LibraryEntryIdSchema.nullable(),
})
    .openapi('VenueCustomApp', { description: 'A custom app and whether a venue already offers it (spec/13 §9).' });
export const VenueCustomAppListSchema = paginated(VenueCustomAppSchema, 'VenueCustomAppList');
// ---------------------------------------------------------------------------------------------
// The routes
// ---------------------------------------------------------------------------------------------
const forbidden = problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required');
const notFound = problemResponse('No such custom app in this organisation.', 'not-found');
const noOrganization = problemResponse('No such organisation, or not one you belong to.', 'not-found');
const nameTaken = problemResponse('Another custom app in this organisation already has that name.', 'conflict');
const json = (schema) => ({
    body: { content: { [JSON_CONTENT_TYPE]: { schema } }, required: true },
});
export const listCustomAppsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/custom-apps',
    operationId: 'listCustomApps',
    tags: ['library'],
    summary: "List the organisation's custom apps",
    description: 'The organisation’s unarchived custom apps, oldest first (spec/13 §9). Requires `library:read`.',
    [PERMISSION_EXTENSION]: LIBRARY_READ,
    request: { params: OrganizationScopeParamsSchema, query: PageQuerySchema },
    responses: {
        200: jsonResponse(CustomAppListSchema, 'A page of custom apps.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: noOrganization,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createCustomAppRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/custom-apps',
    operationId: 'createCustomApp',
    tags: ['library'],
    summary: 'Create a custom app',
    description: 'Creates an app the organisation’s venues can add to their libraries: its name, suggested kiosk category, launch ' +
        'recipes (exe/uri/store/lnk, paths limited to the variables a station resolves) and detection rules. The name is ' +
        'unique among the organisation’s unarchived custom apps (`409`). Requires `library:manage`. Audited.',
    [PERMISSION_EXTENSION]: LIBRARY_MANAGE,
    request: { params: OrganizationScopeParamsSchema, ...json(CreateCustomAppSchema) },
    responses: {
        201: jsonResponse(CustomAppSchema, 'The custom app that was created.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: noOrganization,
        409: nameTaken,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const updateCustomAppRoute = createRoute({
    method: 'patch',
    path: '/v1/organizations/{organizationId}/custom-apps/{customAppId}',
    operationId: 'updateCustomApp',
    tags: ['library'],
    summary: 'Edit a custom app',
    description: 'Only the fields sent change; `launchRecipes` replaces the set. Every venue that offers it picks the change up in ' +
        'its next station bundle. An archived app cannot be edited (`404`). Requires `library:manage`. Audited.',
    [PERMISSION_EXTENSION]: LIBRARY_MANAGE,
    request: { params: CustomAppParamsSchema, ...json(UpdateCustomAppSchema) },
    responses: {
        200: jsonResponse(CustomAppSchema, 'The custom app as it is now.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: notFound,
        409: nameTaken,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const archiveCustomAppRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/custom-apps/{customAppId}/archive',
    operationId: 'archiveCustomApp',
    tags: ['library'],
    summary: 'Archive a custom app',
    description: 'Hides the app from the add picker and takes it out of every station’s bundle. Library entries that offer it ' +
        'stay until a venue removes them. Archiving an archived app is a no-op. Requires `library:manage`. Audited.',
    [PERMISSION_EXTENSION]: LIBRARY_MANAGE,
    request: { params: CustomAppParamsSchema },
    responses: {
        200: jsonResponse(CustomAppSchema, 'The archived custom app.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const createCustomAppIconUploadRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/custom-apps/{customAppId}/icon',
    operationId: 'createCustomAppIconUpload',
    tags: ['library'],
    summary: 'Reserve an icon upload for a custom app',
    description: 'Returns a short-lived signed URL to PUT a square PNG or WebP icon (at most 1 MB) to our object storage; the ' +
        'signature covers the declared `Content-Type` and `Content-Length`. Confirm with `POST …/icon/{uploadId}/complete`. ' +
        'Object storage not configured answers `500`. Requires `library:manage`.',
    [PERMISSION_EXTENSION]: LIBRARY_MANAGE,
    request: { params: CustomAppParamsSchema, ...json(CreateAppIconUploadSchema) },
    responses: {
        201: jsonResponse(AppIconUploadSchema, 'Where to upload the icon.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: notFound,
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const completeCustomAppIconUploadRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/custom-apps/{customAppId}/icon/{uploadId}/complete',
    operationId: 'completeCustomAppIconUpload',
    tags: ['library'],
    summary: 'Confirm a custom app icon upload',
    description: 'Reads the upload back, checks it really is a PNG or WebP within 1 MB, and copies it to a content-addressed, ' +
        'immutable key under the organisation (`orgs/{o}/apps/{id}/{sha256}.{ext}`); the staged upload is removed either ' +
        'way. A missing or non-image upload is `422`. Requires `library:manage`. Audited.',
    [PERMISSION_EXTENSION]: LIBRARY_MANAGE,
    request: { params: CustomAppIconUploadParamsSchema },
    responses: {
        200: jsonResponse(CustomAppSchema, 'The custom app with its new icon.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: notFound,
        422: problemResponse('The upload is missing, too large, or not a PNG or WebP.', 'unprocessable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const browseVenueCustomAppsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/venues/{venueId}/custom-apps',
    operationId: 'browseVenueCustomApps',
    tags: ['library'],
    summary: "The organisation's custom apps, as a venue adds them",
    description: 'The organisation’s unarchived custom apps, oldest first, each marked whether this venue already offers it — ' +
        'what the library’s add picker shows beside the catalogue’s apps. Requires `library:read`.',
    [PERMISSION_EXTENSION]: LIBRARY_READ,
    request: { params: VenueScopeParamsSchema, query: PageQuerySchema },
    responses: {
        200: jsonResponse(VenueCustomAppListSchema, 'A page of custom apps, each flagged whether the venue offers it.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such venue in this organisation, or not one you can see.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const CUSTOM_APP_ROUTES = [
    listCustomAppsRoute,
    createCustomAppRoute,
    updateCustomAppRoute,
    archiveCustomAppRoute,
    createCustomAppIconUploadRoute,
    completeCustomAppIconUploadRoute,
    browseVenueCustomAppsRoute,
];
//# sourceMappingURL=custom-apps.js.map